<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useMarketing } from '@/helpers/useMarketing';
import { AppValues } from '@/helpers/const';
import type { CustomerAwareOfBridgit } from '@/types/api/api-enums';

const props = withDefaults(
  defineProps<{
    brokerId?: string;
    originType?: string;
    displayContext?: 'broker' | 'customer';
  }>(),
  {
    brokerId: undefined,
    originType: undefined,
    displayContext: 'broker',
  },
);

const emit = defineEmits<{
  (e: 'update:referralTypeId', value: string | null): void;
  (e: 'update:referralOtherDescription', value: string | null): void;
  (e: 'update:customerAwareOfBridgit', value: CustomerAwareOfBridgit | null): void;
}>();

const {
  brokerMarketingData,
  referralTypes,
  formData,
  fetchBrokerMarketingData,
  fetchReferralTypes,
  setMarketingFormData,
} = useMarketing();

const isLoading = ref(false);
const isExpanded = ref(false);

const BRIDGIT_LMG_ORIGINS = ['bridgit', 'lmg'];

/**
 * Determines if the customer awareness question should be displayed.
 * Only shown for Bridgit and LMG origin types.
 * @returns {boolean} True if originType is 'bridgit' or 'lmg'
 */
const shouldShowCustomerAware = computed(() => {
  const origin = props.originType?.toLowerCase();
  return !!origin && BRIDGIT_LMG_ORIGINS.includes(origin);
});

/**
 * Determines if the "How did you hear about us?" question should be displayed.
 * - For customer context: Always shown if referral types are available
 * - For broker context: Hidden if broker already answered (originator-level record exists)
 * @returns {boolean} True if the referral type question should be shown
 */
const shouldShowHowDidYouHear = computed(() => {
  // For customer context, always show if referral types are available
  if (props.displayContext === 'customer') {
    return referralTypes.value.length > 0;
  }
  // For broker context, hide if broker already answered (originator-level record exists)
  if (brokerMarketingData.value?.referralTypeId) {
    return false;
  }
  return referralTypes.value.length > 0;
});

const shouldShowCustomerAwareQuestion = computed(() => {
  return shouldShowCustomerAware.value;
});

/**
 * Checks if broker has existing marketing data and the form is in collapsed state.
 * Used to determine when to show the collapse button and display existing data summary.
 * @returns {boolean} True if broker has answered and form is not expanded
 */
const hasExistingData = computed(() => {
  return brokerMarketingData.value?.referralTypeId != null && !isExpanded.value;
});

const selectedReferralTypeId = computed({
  get: () => formData.value.referralTypeId || undefined,
  set: (value: string | undefined) => {
    setMarketingFormData({ referralTypeId: value || undefined });
    emit('update:referralTypeId', value ?? null);
  },
});

const selectedCustomerAware = computed({
  get: () => formData.value.customerAwareOfBridgit || undefined,
  set: (value: CustomerAwareOfBridgit | undefined) => {
    setMarketingFormData({ customerAwareOfBridgit: value || undefined });
    emit('update:customerAwareOfBridgit', value ?? null);
  },
});

const referralTypeOptions = computed(() => {
  return referralTypes.value.reduce((acc, rt) => {
    acc[rt.id] = rt.type;
    return acc;
  }, {} as Record<string, string>);
});

/**
 * Checks if the "Other" referral type option is currently selected.
 * Used to conditionally show the text input for custom referral description.
 * @returns {boolean} True if "Other" option is selected
 */
const isOtherSelected = computed(() => {
  if (!selectedReferralTypeId.value) return false;
  const selectedType = referralTypes.value.find((rt) => rt.id === selectedReferralTypeId.value);
  return selectedType?.type?.toLowerCase() === 'other';
});

const selectedOtherDescription = computed({
  get: () => formData.value.referralOtherDescription || '',
  set: (value: string) => {
    setMarketingFormData({ referralOtherDescription: value || undefined });
    emit('update:referralOtherDescription', value || null);
  },
});

const customerAwareOptions = {
  yes: 'Yes',
  no: 'No',
  not_sure: 'Not Sure',
};

/**
 * Loads marketing data for the component.
 * Fetches broker marketing data (if broker context) and referral types.
 * Pre-populates form with existing broker data and emits values to parent
 * so they're included in submission even when the form is hidden.
 */
const loadMarketingData = async () => {
  isLoading.value = true;
  try {
    // Fetch broker marketing data only in broker context
    if (props.displayContext === 'broker' && props.brokerId) {
      await fetchBrokerMarketingData();
    }

    // Fetch referral types based on display context
    await fetchReferralTypes(props.displayContext, props.originType, props.brokerId);

    // Set initial values from broker data if available
    if (brokerMarketingData.value?.referralTypeId) {
      setMarketingFormData({
        referralTypeId: brokerMarketingData.value.referralTypeId,
        referralOtherDescription: brokerMarketingData.value.referralOtherDescription ?? undefined,
      });
      // Emit to parent so data is included in submission even when hidden
      emit('update:referralTypeId', brokerMarketingData.value.referralTypeId);
      if (brokerMarketingData.value.referralOtherDescription) {
        emit('update:referralOtherDescription', brokerMarketingData.value.referralOtherDescription);
      }
    }
  } catch (error) {
    console.error('Failed to load marketing data:', error);
  } finally {
    isLoading.value = false;
  }
};

const toggleExpand = () => {
  isExpanded.value = !isExpanded.value;
};

watch(
  () => [props.brokerId, props.originType, props.displayContext],
  () => {
    loadMarketingData();
  },
  { immediate: false },
);

// Clear other description when switching away from "Other"
watch(isOtherSelected, (isOther) => {
  if (!isOther && formData.value.referralOtherDescription) {
    selectedOtherDescription.value = '';
  }
});

onMounted(() => {
  loadMarketingData();
});
</script>

<template>
  <div class="w-full">
    <div v-if="isLoading" class="flex items-center gap-2 text-sm text-gray-500">
      <span>Loading marketing data...</span>
    </div>

    <template v-else>
      <!-- "How did you hear about us" options - shown only if the broker has not previously answered -->
      <div v-if="shouldShowHowDidYouHear" class="flex flex-col gap-6">
        <div class="flex flex-col gap-2">
          <label class="mb-2 text-2xl font-medium leading-8 text-gray-800">
            How did you <span class="text-highlight"><BxHighlight>hear about us</BxHighlight></span
            >?
          </label>
          <BxRadio
            id="marketing-referral-type"
            v-model="selectedReferralTypeId"
            data-test-id="marketing-referral-type"
            class="form-control"
            :options="referralTypeOptions"
            label=""
            block
            @update:model-value="(value) => (selectedReferralTypeId = (value as string) || undefined)"
          />
        </div>

        <div v-if="isOtherSelected" class="flex flex-col gap-2">
          <BxInput
            id="marketing-other-description"
            v-model="selectedOtherDescription"
            data-test-id="marketing-other-description"
            :placeholder="`How did you discover ${AppValues.COMPANY_NAME}?`"
          />
        </div>

        <div v-if="hasExistingData && displayContext === 'broker'" class="mt-2 flex justify-end">
          <BxButton variant="link" class="text-sm" data-test-id="marketing-collapse-button" @click="toggleExpand">
            Collapse
          </BxButton>
        </div>
      </div>

      <!-- Customer Aware Question - always shown when originType is Bridgit or LMG -->
      <div
        v-if="shouldShowCustomerAwareQuestion"
        class="flex flex-col gap-6"
        :class="{ 'mt-4': hasExistingData || shouldShowHowDidYouHear }"
      >
        <div class="flex flex-col gap-2">
          <label class="mb-2 text-2xl font-medium leading-8 text-gray-800">
            Was your customer aware of
            <BxHighlight>{{ AppValues.COMPANY_NAME }} before you met with them </BxHighlight>?
          </label>
          <BxRadio
            id="marketing-customer-aware"
            v-model="selectedCustomerAware"
            data-test-id="marketing-customer-aware"
            class="form-control"
            :options="customerAwareOptions"
            label=""
            block
            @update:model-value="(value) => (selectedCustomerAware = (value as CustomerAwareOfBridgit) || undefined)"
          />
        </div>
      </div>
    </template>
  </div>
</template>
